import api, { getAuthToken } from "./client";

async function requestJson(endpoint, method = "GET", payload = null) {
  const token = getAuthToken();
  const isFormData = typeof FormData !== "undefined" && payload instanceof FormData;
  const response = await fetch(api.buildUrl(endpoint), {
    method,
    headers: {
      Accept: "application/json",
      ...(!isFormData && payload ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
    ...(payload
      ? {
          body: isFormData ? payload : JSON.stringify(payload),
        }
      : {}),
  });

  const text = await response.text();
  let data = {};

  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = {};
  }

  if (!response.ok) {
    const validationMessage =
      data?.errors && typeof data.errors === "object"
        ? Object.values(data.errors).flat().find((item) => typeof item === "string")
        : "";

    throw {
      status: response.status,
      message: validationMessage || data?.message || "Request failed",
      body: data,
    };
  }

  return data;
}

function isFileLike(value) {
  return typeof File !== "undefined" && value instanceof File;
}

function buildMultipartRequestBody(values, uploadKeys = ["image_file"]) {
  const hasUploads = uploadKeys.some((key) => isFileLike(values?.[key]));

  if (!hasUploads) {
    return values;
  }

  const formData = new FormData();

  Object.entries(values || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") {
      return;
    }

    if (typeof value === "boolean") {
      formData.append(key, value ? "1" : "0");
      return;
    }

    formData.append(key, value);
  });

  return formData;
}

function normalizeCatalogAttribute(attribute) {
  return {
    id: Number(attribute.id || 0),
    slug: attribute.slug || "",
    nameAr: attribute.name_ar || "",
    nameEn: attribute.name_en || "",
  };
}

function normalizeCatalogAttributeValue(value) {
  return {
    id: Number(value.id || 0),
    attributeId: Number(value.attribute_id || 0),
    slug: value.slug || "",
    valueAr: value.value_ar || "",
    valueEn: value.value_en || "",
    sortOrder: Number(value.sort_order || 0),
  };
}

function normalizeSellerVariant(variant) {
  const options = Array.isArray(variant.options) ? variant.options : [];
  const primaryOption = options[0] || null;
  const colorOption = options.find((option) => option.group === "color") || null;
  const sizeOption = options.find((option) => option.group === "size") || null;

  return {
    id: Number(variant.id || 0),
    productId: Number(variant.product_id || 0),
    attributeId: Number(variant.attribute_id || 0),
    valueId: primaryOption ? Number(primaryOption.id || 0) : 0,
    valueNameAr: primaryOption?.label_ar || "",
    valueNameEn: primaryOption?.label_en || "",
    colorId: colorOption ? Number(colorOption.id || 0) : 0,
    colorNameAr: colorOption?.label_ar || "",
    colorNameEn: colorOption?.label_en || "",
    sizeId: sizeOption ? Number(sizeOption.id || 0) : 0,
    sizeNameAr: sizeOption?.label_ar || "",
    sizeNameEn: sizeOption?.label_en || "",
    sku: variant.sku || "",
    price: Number(variant.price || 0),
    salePrice: Number(variant.sale_price || 0),
    stock: Number(variant.stock || 0),
    lowStockAlert: Number(variant.low_stock_alert || 0),
    image: variant.image_url || variant.image || "",
    rawImage: variant.image || "",
    options,
  };
}

function normalizeProfile(profile) {
  return {
    id: Number(profile.id || 0),
    userId: Number(profile.user_id || 0),
    providerRole: profile.provider_role || "",
    status: profile.status || "pending",
    displayName: profile.display_name || "",
    headline: profile.headline || "",
    bio: profile.bio || "",
    experienceYears:
      profile.experience_years !== null && profile.experience_years !== undefined
        ? Number(profile.experience_years || 0)
        : null,
    portfolioUrl: profile.portfolio_url || "",
    cvUrl: profile.cv_url || "",
    companyName: profile.company_name || "",
    submittedAt: profile.submitted_at || "",
    reviewedAt: profile.reviewed_at || "",
    rejectionReason: profile.rejection_reason || "",
    adminNotes: profile.admin_notes || "",
    reviewedBy: profile.reviewed_by || null,
  };
}

function normalizeContentItem(item) {
  return {
    type: item.type || "",
    id: Number(item.id || 0),
    titleAr: item.title_ar || "",
    titleEn: item.title_en || "",
    slug: item.slug || "",
    approvalStatus: item.approval_status || "approved",
    isActive: Boolean(item.is_active ?? true),
    submittedAt: item.submitted_at || "",
    approvedAt: item.approved_at || "",
    rejectionReason: item.rejection_reason || "",
  };
}

function normalizeProviderService(service) {
  return {
    id: Number(service.id || 0),
    freelancerId:
      service.freelancer_id !== null && service.freelancer_id !== undefined
        ? Number(service.freelancer_id || 0)
        : null,
    titleAr: service.title_ar || "",
    titleEn: service.title_en || "",
    slug: service.slug || "",
    shortDescriptionAr: service.short_description_ar || "",
    shortDescriptionEn: service.short_description_en || "",
    descriptionAr: service.description_ar || "",
    descriptionEn: service.description_en || "",
    price: Number(service.price || 0),
    currency: service.currency || "USD",
    deliveryDays: Number(service.delivery_days || 0),
    revisions: Number(service.revisions || 0),
    coverImage: service.cover_image || "",
    isActive: Boolean(service.is_active ?? true),
    approvalStatus: service.approval_status || "pending",
    submittedAt: service.submitted_at || "",
    approvedAt: service.approved_at || "",
    rejectionReason: service.rejection_reason || "",
    isFeatured: Boolean(service.is_featured),
    ordersCount: Number(service.orders_count || 0),
    createdAt: service.created_at || "",
    updatedAt: service.updated_at || "",
  };
}

function normalizeProviderConsultation(consultation) {
  return {
    id: Number(consultation.id || 0),
    providerUserId:
      consultation.provider_user_id !== null && consultation.provider_user_id !== undefined
        ? Number(consultation.provider_user_id || 0)
        : null,
    titleAr: consultation.title_ar || "",
    titleEn: consultation.title_en || "",
    slug: consultation.slug || "",
    shortDescriptionAr: consultation.short_description_ar || "",
    shortDescriptionEn: consultation.short_description_en || "",
    descriptionAr: consultation.description_ar || "",
    descriptionEn: consultation.description_en || "",
    price: Number(consultation.price || 0),
    durationMinutes: Number(consultation.duration_minutes || 0),
    coverImage: consultation.cover_image || "",
    meetingProvider: consultation.meeting_provider || "",
    category: consultation.category || "",
    isActive: Boolean(consultation.is_active ?? true),
    approvalStatus: consultation.approval_status || "pending",
    submittedAt: consultation.submitted_at || "",
    approvedAt: consultation.approved_at || "",
    rejectionReason: consultation.rejection_reason || "",
    isFeatured: Boolean(consultation.is_featured),
    bookingsCount: Number(consultation.bookings_count || 0),
    createdAt: consultation.created_at || "",
  };
}

function normalizeProviderCourseLesson(lesson) {
  return {
    id: Number(lesson.id || 0),
    courseId:
      lesson.course_id !== null && lesson.course_id !== undefined
        ? Number(lesson.course_id || 0)
        : null,
    sectionId:
      lesson.section_id !== null && lesson.section_id !== undefined
        ? Number(lesson.section_id || 0)
        : null,
    titleAr: lesson.title_ar || "",
    titleEn: lesson.title_en || "",
    slug: lesson.slug || "",
    lessonType: lesson.lesson_type || "video",
    videoUrl: lesson.video_url || "",
    contentAr: lesson.content_ar || "",
    contentEn: lesson.content_en || "",
    attachmentPath: lesson.attachment_path || "",
    durationSeconds:
      lesson.duration_seconds !== null && lesson.duration_seconds !== undefined
        ? Number(lesson.duration_seconds || 0)
        : null,
    isPreview: Boolean(lesson.is_preview),
    sortOrder: Number(lesson.sort_order || 0),
  };
}

function normalizeProviderCourseSection(section) {
  return {
    id: Number(section.id || 0),
    courseId:
      section.course_id !== null && section.course_id !== undefined
        ? Number(section.course_id || 0)
        : null,
    titleAr: section.title_ar || "",
    titleEn: section.title_en || "",
    sortOrder: Number(section.sort_order || 0),
    lessons: Array.isArray(section.lessons)
      ? section.lessons.map(normalizeProviderCourseLesson)
      : [],
  };
}

function normalizeProviderCourse(course) {
  return {
    id: Number(course.id || 0),
    titleAr: course.title_ar || "",
    titleEn: course.title_en || "",
    slug: course.slug || "",
    instructorUserId:
      course.instructor_user_id !== null && course.instructor_user_id !== undefined
        ? Number(course.instructor_user_id || 0)
        : null,
    shortDescriptionAr: course.short_description_ar || "",
    shortDescriptionEn: course.short_description_en || "",
    descriptionAr: course.description_ar || "",
    descriptionEn: course.description_en || "",
    thumbnail: course.thumbnail || "",
    promoVideoUrl: course.promo_video_url || "",
    price: Number(course.price || 0),
    salePrice:
      course.sale_price !== null && course.sale_price !== undefined
        ? Number(course.sale_price || 0)
        : null,
    isFree: Boolean(course.is_free),
    isActive: Boolean(course.is_active ?? true),
    approvalStatus: course.approval_status || "pending",
    submittedAt: course.submitted_at || "",
    approvedAt: course.approved_at || "",
    rejectionReason: course.rejection_reason || "",
    level: course.level || "beginner",
    durationText: course.duration_text || "",
    instructorName: course.instructor_name || "",
    category: course.category || "",
    requirementsAr: course.requirements_ar || "",
    requirementsEn: course.requirements_en || "",
    whatYouWillLearnAr: course.what_you_will_learn_ar || "",
    whatYouWillLearnEn: course.what_you_will_learn_en || "",
    enrollmentsCount: Number(course.enrollments_count || 0),
    lessonsCount: Number(course.lessons_count || 0),
    createdAt: course.created_at || "",
    sections: Array.isArray(course.sections)
      ? course.sections.map(normalizeProviderCourseSection)
      : [],
  };
}

function normalizeProviderJob(job) {
  return {
    id: Number(job.id || 0),
    titleAr: job.title_ar || "",
    titleEn: job.title_en || "",
    slug: job.slug || "",
    employerUserId:
      job.employer_user_id !== null && job.employer_user_id !== undefined
        ? Number(job.employer_user_id || 0)
        : null,
    shortDescriptionAr: job.short_description_ar || "",
    shortDescriptionEn: job.short_description_en || "",
    descriptionAr: job.description_ar || "",
    descriptionEn: job.description_en || "",
    company: job.company || "",
    salaryMin:
      job.salary_min !== null && job.salary_min !== undefined
        ? Number(job.salary_min || 0)
        : null,
    salaryMax:
      job.salary_max !== null && job.salary_max !== undefined
        ? Number(job.salary_max || 0)
        : null,
    salaryText: job.salary_text || "",
    employmentType: job.employment_type || "full-time",
    location: job.location || "",
    experienceLevel: job.experience_level || "",
    coverImage: job.cover_image || "",
    applicationDeadline: job.application_deadline || "",
    isActive: Boolean(job.is_active ?? true),
    approvalStatus: job.approval_status || "approved",
    submittedAt: job.submitted_at || "",
    approvedAt: job.approved_at || "",
    rejectionReason: job.rejection_reason || "",
    isFeatured: Boolean(job.is_featured),
    applicationsCount: Number(job.applications_count || 0),
    createdAt: job.created_at || "",
    updatedAt: job.updated_at || "",
  };
}

function normalizeSellerProduct(product) {
  return {
    id: Number(product.id || 0),
    sellerUserId:
      product.seller_user_id !== null && product.seller_user_id !== undefined
        ? Number(product.seller_user_id || 0)
        : null,
    nameAr: product.name_ar || "",
    nameEn: product.name_en || "",
    descriptionAr: product.description_ar || "",
    descriptionEn: product.description_en || "",
    price: Number(product.price || 0),
    salePrice: Number(product.sale_price || 0),
    color: product.color || "",
    image: product.image_url || product.image || "",
    rawImage: product.image || "",
    stock: Number(product.stock || 0),
    categoryId: Number(product.category_id || 0),
    categoryNameEn: product.category_name_en || "",
    categoryNameAr: product.category_name_ar || "",
    isDigital: Boolean(product.is_digital),
    digitalFilePath: product.digital_file_path || "",
    status: product.status || "active",
    approvalStatus: product.approval_status || "pending",
    submittedAt: product.submitted_at || "",
    approvedAt: product.approved_at || "",
    rejectionReason: product.rejection_reason || "",
    variants: Array.isArray(product.variants)
      ? product.variants.map(normalizeSellerVariant)
      : [],
  };
}

function normalizePayoutMethod(method) {
  return {
    id: Number(method.id || 0),
    method: method.method || "",
    accountLabel: method.account_label || "",
    accountName: method.account_name || "",
    destinationEmail: method.destination_email || "",
    destinationReference: method.destination_reference || "",
    isDefault: Boolean(method.is_default),
    status: method.status || "active",
    meta: method.meta && typeof method.meta === "object" ? method.meta : {},
    createdAt: method.created_at || "",
    updatedAt: method.updated_at || "",
  };
}

const LINKED_PAYPAL_PAYOUT_REASON =
  "PayPal withdrawals require the provider to sign in and link a personal payout account. Manual payout details are disabled until linked-account onboarding is enabled.";

function normalizePayPalPayoutMethod(method) {
  return method === "test_paypal" ? "test_paypal" : "paypal";
}

function normalizeBankAccount(account) {
  return {
    id: Number(account.id || 0),
    userId: Number(account.user_id || 0),
    fullName: account.full_name || "",
    iban: account.iban || "",
    countryCode: account.country_code || "",
    currency: account.currency || "",
    bankName: account.bank_name || "",
    swiftCode: account.swift_code || "",
    addressLine: account.address_line || "",
    city: account.city || "",
    postalCode: account.postal_code || "",
    isDefault: Boolean(account.is_default),
    status: account.status || "active",
    createdAt: account.created_at || "",
    updatedAt: account.updated_at || "",
  };
}

function normalizeWithdrawalRequest(withdrawal) {
  return {
    id: Number(withdrawal.id || 0),
    payoutMethodId:
      withdrawal.payout_method_id !== null && withdrawal.payout_method_id !== undefined
        ? Number(withdrawal.payout_method_id || 0)
        : null,
    withdrawalMethod: withdrawal.withdrawal_method || "",
    destinationId:
      withdrawal.destination_id !== null && withdrawal.destination_id !== undefined
        ? Number(withdrawal.destination_id || 0)
        : null,
    amount: Number(withdrawal.amount || 0),
    platformFee: Number(withdrawal.platform_fee || 0),
    netAmount: Number(withdrawal.net_amount || withdrawal.amount || 0),
    currency: withdrawal.currency || "USD",
    status: withdrawal.status || "pending",
    notes: withdrawal.notes || "",
    adminNotes: withdrawal.admin_notes || "",
    providerReference: withdrawal.provider_reference || "",
    providerStatus: withdrawal.provider_status || "",
    reviewRequired: Boolean(withdrawal.review_required),
    reviewReason: withdrawal.review_reason || "",
    reviewedAt: withdrawal.reviewed_at || "",
    autoProcessed: Boolean(withdrawal.auto_processed),
    failureReason: withdrawal.failure_reason || "",
    processedAt: withdrawal.processed_at || "",
    rejectionReason: withdrawal.rejection_reason || "",
    createdAt: withdrawal.created_at || "",
    destinationSnapshot: withdrawal.destination_snapshot || null,
    bankAccount: withdrawal.bank_account ? normalizeBankAccount(withdrawal.bank_account) : null,
    transfers: Array.isArray(withdrawal.transfers) ? withdrawal.transfers : [],
    payoutMethod: withdrawal.payout_method
      ? {
          id: Number(withdrawal.payout_method.id || 0),
          method: withdrawal.payout_method.method || "",
          accountLabel: withdrawal.payout_method.account_label || "",
          destinationEmail: withdrawal.payout_method.destination_email || "",
          destinationReference: withdrawal.payout_method.destination_reference || "",
        }
      : null,
  };
}

export async function fetchProviderDashboard() {
  const response = await api.apiGet("v1/provider-dashboard");
  const payload = response?.data || {};

  return {
    profiles: Array.isArray(payload.profiles) ? payload.profiles.map(normalizeProfile) : [],
    content: {
      courses: Array.isArray(payload.content?.courses)
        ? payload.content.courses.map(normalizeContentItem)
        : [],
      consultations: Array.isArray(payload.content?.consultations)
        ? payload.content.consultations.map(normalizeContentItem)
        : [],
      services: Array.isArray(payload.content?.services)
        ? payload.content.services.map(normalizeContentItem)
        : [],
      jobs: Array.isArray(payload.content?.jobs)
        ? payload.content.jobs.map(normalizeContentItem)
        : [],
      products: Array.isArray(payload.content?.products)
        ? payload.content.products.map(normalizeContentItem)
        : [],
    },
    summary: payload.summary || {},
  };
}

export async function submitProviderApplication(values) {
  const response = await api.apiPost("v1/provider-profiles/apply", values);
  return {
    message: response?.message || "Provider application submitted successfully.",
    profile: normalizeProfile(response?.data || {}),
  };
}

export async function fetchProviderEarnings() {
  const response = await api.apiGet("v1/provider-earnings");
  const payload = response?.data || {};

  return {
    summary: payload.summary || {},
    methods: Array.isArray(payload.methods) ? payload.methods.map(normalizePayoutMethod) : [],
    bankAccounts: Array.isArray(payload.bank_accounts)
      ? payload.bank_accounts.map(normalizeBankAccount)
      : [],
    ibanSettings:
      payload.iban_settings && typeof payload.iban_settings === "object"
        ? {
            globalEnabled: Boolean(payload.iban_settings.global_enabled),
            firstManualReviewCount: Number(
              payload.iban_settings.first_manual_review_count || 0,
            ),
            autoProcessAfterThreshold: Boolean(
              payload.iban_settings.auto_process_after_threshold,
            ),
            feeRate: Number(payload.iban_settings.fee_rate || 0.0003),
            userEnabled: Boolean(payload.iban_settings.user_enabled ?? true),
            forceManualReview: Boolean(payload.iban_settings.force_manual_review),
            manualReviewThreshold:
              payload.iban_settings.manual_review_threshold !== null &&
              payload.iban_settings.manual_review_threshold !== undefined
                ? Number(payload.iban_settings.manual_review_threshold || 0)
                : null,
            blockedReason: payload.iban_settings.blocked_reason || "",
          }
        : {
            globalEnabled: false,
            firstManualReviewCount: 5,
            autoProcessAfterThreshold: true,
            feeRate: 0.0003,
            userEnabled: true,
            forceManualReview: false,
            manualReviewThreshold: null,
            blockedReason: "",
          },
    countryMethodAvailability:
      payload.country_method_availability && typeof payload.country_method_availability === "object"
        ? payload.country_method_availability
        : {
            country_code: "",
            country_known: false,
            payouts: {
              types: {
                legacy: { supported: true, reason: null },
                iban_wise: { supported: true, reason: null },
              },
              legacy_methods: {
                paypal: { supported: false, reason: LINKED_PAYPAL_PAYOUT_REASON },
                test_paypal: { supported: false, reason: "PayPal sandbox mode is admin-only." },
                stripe: { supported: true, reason: null },
              },
            },
          },
    requests: Array.isArray(payload.requests)
      ? payload.requests.map(normalizeWithdrawalRequest)
      : [],
  };
}

export async function createProviderPayoutMethod(values) {
  const response = await requestJson("v1/provider-payout-methods", "POST", values);
  return {
    message: response?.message || "Payout method saved successfully.",
    method: normalizePayoutMethod(response?.data || {}),
  };
}

export async function createStripeConnectLink() {
  const response = await requestJson("v1/provider-payout-methods/stripe/connect-link", "POST");
  return {
    message: response?.message || "Stripe payout onboarding link created successfully.",
    method: response?.data?.method ? normalizePayoutMethod(response.data.method) : null,
    onboardingUrl: response?.data?.onboarding_url || "",
    expiresAt: response?.data?.expires_at || null,
  };
}

export async function fetchStripeConnectStatus() {
  const response = await requestJson("v1/provider-payout-methods/stripe/status");
  return {
    message: response?.message || "Stripe payout status fetched successfully.",
    method: response?.data ? normalizePayoutMethod(response.data) : null,
  };
}

export async function createStripeDashboardLink() {
  const response = await requestJson("v1/provider-payout-methods/stripe/dashboard-link", "POST");
  return {
    message: response?.message || "Stripe dashboard link created successfully.",
    method: response?.data?.method ? normalizePayoutMethod(response.data.method) : null,
    dashboardUrl: response?.data?.dashboard_url || "",
  };
}

export async function fetchPayPalPayoutLinkConfig(method = "paypal") {
  const normalizedMethod = normalizePayPalPayoutMethod(method);
  const response = await requestJson(
    `v1/provider-payout-methods/paypal/link-config?method=${encodeURIComponent(normalizedMethod)}`,
  );
  const payload = response?.data || {};

  return {
    method: payload.method || normalizedMethod,
    enabled: Boolean(payload.enabled),
    mode: payload.mode || "live",
    env: payload.env || (payload.mode === "sandbox" ? "sandbox" : "production"),
    merchantId: payload.merchant_id || "",
    clientIds:
      payload.client_ids && typeof payload.client_ids === "object"
        ? {
            production: payload.client_ids.production || "",
            sandbox: payload.client_ids.sandbox || "",
          }
        : { production: "", sandbox: "" },
    reason: payload.reason || "",
  };
}

export async function completePayPalPayoutLink(code, method = "paypal") {
  const normalizedMethod = normalizePayPalPayoutMethod(method);
  const response = await requestJson("v1/provider-payout-methods/paypal/complete-link", "POST", {
    code,
    method: normalizedMethod,
  });

  return {
    message: response?.message || "PayPal payout account linked successfully.",
    method: normalizePayoutMethod(response?.data || {}),
  };
}

export async function updateProviderPayoutMethod(id, values) {
  const response = await requestJson(`v1/provider-payout-methods/${id}`, "PUT", values);
  return {
    message: response?.message || "Payout method updated successfully.",
    method: normalizePayoutMethod(response?.data || {}),
  };
}

export async function deleteProviderPayoutMethod(id) {
  const response = await requestJson(`v1/provider-payout-methods/${id}`, "DELETE");
  return {
    message: response?.message || "Payout method deleted successfully.",
  };
}

export async function createProviderWithdrawalRequest(values) {
  const response = await requestJson("v1/provider-withdrawals", "POST", values);
  return {
    message: response?.message || "Withdrawal request submitted successfully.",
    request: normalizeWithdrawalRequest(response?.data || {}),
  };
}

export async function createIbanWithdrawalRequest(values) {
  const response = await requestJson("v1/withdrawals/iban/request", "POST", values);
  return {
    message: response?.message || "IBAN withdrawal request submitted successfully.",
    request: normalizeWithdrawalRequest(response?.data || {}),
  };
}

export async function fetchUserBankAccounts() {
  const response = await requestJson("v1/bank-accounts");
  const payload = response?.data || [];
  return Array.isArray(payload) ? payload.map(normalizeBankAccount) : [];
}

export async function createUserBankAccount(values) {
  const response = await requestJson("v1/bank-accounts", "POST", values);
  return {
    message: response?.message || "Bank account saved successfully.",
    bankAccount: normalizeBankAccount(response?.data || {}),
  };
}

export async function updateUserBankAccount(id, values) {
  const response = await requestJson(`v1/bank-accounts/${id}`, "PUT", values);
  return {
    message: response?.message || "Bank account updated successfully.",
    bankAccount: normalizeBankAccount(response?.data || {}),
  };
}

export async function deleteUserBankAccount(id) {
  const response = await requestJson(`v1/bank-accounts/${id}`, "DELETE");
  return {
    message: response?.message || "Bank account deleted successfully.",
  };
}

export async function setDefaultUserBankAccount(id) {
  const response = await requestJson(`v1/bank-accounts/${id}/default`, "POST");
  return {
    message: response?.message || "Default bank account updated successfully.",
    bankAccount: normalizeBankAccount(response?.data || {}),
  };
}

export async function fetchSellerProductMetadata() {
  const response = await requestJson("v1/seller-products/meta");
  const payload = response?.data || {};

  return {
    enabled: Boolean(payload.enabled ?? true),
    categories: Array.isArray(payload.categories)
      ? payload.categories.map((category) => ({
          id: Number(category.id || 0),
          nameEn: category.name_en || "",
          nameAr: category.name_ar || "",
        }))
      : [],
    attributes: Array.isArray(payload.attributes)
      ? payload.attributes.map(normalizeCatalogAttribute)
      : [],
    attributeValues: Array.isArray(payload.attribute_values)
      ? payload.attribute_values.map(normalizeCatalogAttributeValue)
      : [],
  };
}

export async function fetchSellerProducts() {
  const response = await requestJson("v1/seller-products");
  const payload = response?.data || [];
  return Array.isArray(payload) ? payload.map(normalizeSellerProduct) : [];
}

export async function createSellerProduct(values) {
  const payload = buildMultipartRequestBody(values, ["image_file", "digital_file"]);
  const response = await requestJson("v1/seller-products", "POST", payload);
  return {
    message: response?.message || "Product submitted for admin review successfully.",
    product: normalizeSellerProduct(response?.data || {}),
  };
}

export async function updateSellerProduct(id, values) {
  const payload = buildMultipartRequestBody(values, ["image_file", "digital_file"]);
  const response =
    payload instanceof FormData
      ? (() => {
          payload.append("_method", "PUT");
          return requestJson(`v1/seller-products/${id}`, "POST", payload);
        })()
      : requestJson(`v1/seller-products/${id}`, "PUT", payload);
  return {
    message: response?.message || "Product updated successfully.",
    product: normalizeSellerProduct(response?.data || {}),
  };
}

export async function deleteSellerProduct(id) {
  const response = await requestJson(`v1/seller-products/${id}`, "DELETE");
  return {
    message: response?.message || "Product deleted successfully.",
  };
}

export async function createSellerProductVariant(productId, values) {
  const payload = buildMultipartRequestBody(values, ["image_file"]);
  const response = await requestJson(`v1/seller-products/${productId}/variants`, "POST", payload);
  return {
    message: response?.message || "Variant created successfully.",
    product: normalizeSellerProduct(response?.data || {}),
  };
}

export async function updateSellerProductVariant(productId, variantId, values) {
  const payload = buildMultipartRequestBody(values, ["image_file"]);
  const response =
    payload instanceof FormData
      ? (() => {
          payload.append("_method", "PUT");
          return requestJson(`v1/seller-products/${productId}/variants/${variantId}`, "POST", payload);
        })()
      : requestJson(`v1/seller-products/${productId}/variants/${variantId}`, "PUT", payload);

  return {
    message: response?.message || "Variant updated successfully.",
    product: normalizeSellerProduct(response?.data || {}),
  };
}

export async function deleteSellerProductVariant(productId, variantId) {
  const response = await requestJson(`v1/seller-products/${productId}/variants/${variantId}`, "DELETE");
  return {
    message: response?.message || "Variant deleted successfully.",
    product: normalizeSellerProduct(response?.data || {}),
  };
}

export async function fetchProviderServices() {
  const response = await requestJson("v1/provider-services");
  const payload = response?.data || [];
  return Array.isArray(payload) ? payload.map(normalizeProviderService) : [];
}

export async function createProviderService(values) {
  const response = await requestJson("v1/provider-services", "POST", values);
  return {
    message: response?.message || "Freelancer service submitted for admin review successfully.",
    service: normalizeProviderService(response?.data || {}),
  };
}

export async function updateProviderService(id, values) {
  const response = await requestJson(`v1/provider-services/${id}`, "PUT", values);
  return {
    message: response?.message || "Freelancer service updated successfully.",
    service: normalizeProviderService(response?.data || {}),
  };
}

export async function deleteProviderService(id) {
  const response = await requestJson(`v1/provider-services/${id}`, "DELETE");
  return {
    message: response?.message || "Freelancer service deleted successfully.",
  };
}

export async function fetchProviderConsultations() {
  const response = await requestJson("v1/provider-consultations");
  const payload = response?.data || [];
  return Array.isArray(payload) ? payload.map(normalizeProviderConsultation) : [];
}

export async function createProviderConsultation(values) {
  const response = await requestJson("v1/provider-consultations", "POST", values);
  return {
    message: response?.message || "Consultation submitted for admin review successfully.",
    consultation: normalizeProviderConsultation(response?.data || {}),
  };
}

export async function updateProviderConsultation(id, values) {
  const response = await requestJson(`v1/provider-consultations/${id}`, "PUT", values);
  return {
    message: response?.message || "Consultation updated successfully.",
    consultation: normalizeProviderConsultation(response?.data || {}),
  };
}

export async function deleteProviderConsultation(id) {
  const response = await requestJson(`v1/provider-consultations/${id}`, "DELETE");
  return {
    message: response?.message || "Consultation deleted successfully.",
  };
}

export async function fetchProviderCourses() {
  const response = await requestJson("v1/provider-courses");
  const payload = response?.data || [];
  return Array.isArray(payload) ? payload.map(normalizeProviderCourse) : [];
}

export async function createProviderCourse(values) {
  const response = await requestJson("v1/provider-courses", "POST", values);
  return {
    message: response?.message || "Course submitted for admin review successfully.",
    course: normalizeProviderCourse(response?.data || {}),
  };
}

export async function updateProviderCourse(id, values) {
  const response = await requestJson(`v1/provider-courses/${id}`, "PUT", values);
  return {
    message: response?.message || "Course updated successfully.",
    course: normalizeProviderCourse(response?.data || {}),
  };
}

export async function deleteProviderCourse(id) {
  const response = await requestJson(`v1/provider-courses/${id}`, "DELETE");
  return {
    message: response?.message || "Course deleted successfully.",
  };
}

export async function fetchProviderJobs() {
  const response = await requestJson("v1/provider-jobs");
  const payload = response?.data || [];
  return Array.isArray(payload) ? payload.map(normalizeProviderJob) : [];
}

export async function createProviderJob(values) {
  const response = await requestJson("v1/provider-jobs", "POST", values);
  return {
    message: response?.message || "Job created successfully.",
    job: normalizeProviderJob(response?.data || {}),
  };
}

export async function updateProviderJob(id, values) {
  const response = await requestJson(`v1/provider-jobs/${id}`, "PUT", values);
  return {
    message: response?.message || "Job updated successfully.",
    job: normalizeProviderJob(response?.data || {}),
  };
}

export async function deleteProviderJob(id) {
  const response = await requestJson(`v1/provider-jobs/${id}`, "DELETE");
  return {
    message: response?.message || "Job deleted successfully.",
  };
}
